import {
  forwardRef,
  memo,
  useState,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { Dropdown, Form } from "react-bootstrap";
import { BsChevronDown } from "react-icons/bs";
import {
  FILTER_ITEM_ACTIVE_STYLE,
  FILTER_ITEM_HOVER_STYLE,
  FILTER_PILL_ACTIVE_STYLE,
  filterOptionClass,
} from "./eventsListShared";

const FILTER_DROPDOWN_POPPER_CONFIG: NonNullable<
  React.ComponentProps<typeof Dropdown.Menu>["popperConfig"]
> = {
  modifiers: [
    {
      name: "sameWidth",
      enabled: true,
      phase: "beforeWrite",
      requires: ["computeStyles"],
      fn({ state }) {
        const w = `${state.rects.reference.width}px`;
        state.styles.popper.width = w;
        state.styles.popper.minWidth = w;
        state.styles.popper.maxWidth = w;
      },
    },
  ],
};

const FilterPillToggle = memo(
  forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
    function FilterPillToggle({ children, className, onClick, ...props }, ref) {
      return (
        <button
          type="button"
          ref={ref}
          className={className}
          onClick={onClick}
          {...props}
        >
          {children}
        </button>
      );
    },
  ),
);

/** Filter option with project hover/selected colors (no Bootstrap blue). */
export function FilterMenuItem({
  selected = false,
  className,
  style,
  onClick,
  children,
}: {
  selected?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const tone = selected
    ? FILTER_ITEM_ACTIVE_STYLE
    : hovered
      ? FILTER_ITEM_HOVER_STYLE
      : undefined;

  return (
    <Dropdown.Item
      as="button"
      type="button"
      className={className}
      style={{ backgroundImage: "none", ...tone, ...style }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={onClick}
    >
      {children}
    </Dropdown.Item>
  );
}

export function FilterDropdown({
  id,
  active,
  label,
  ariaLabel,
  icon,
  style,
  children,
  autoClose = "outside",
  show,
  onToggle,
}: {
  id: string;
  active: boolean;
  label: string;
  ariaLabel?: string;
  icon: string;
  style?: CSSProperties;
  children: ReactNode;
  /** Defaults to "outside" (Events searchable filters). Use true to close on item select. */
  autoClose?: boolean | "inside" | "outside";
  show?: boolean;
  onToggle?: (next: boolean) => void;
}) {
  return (
    <Dropdown
      autoClose={autoClose}
      className="w-100"
      style={style}
      show={show}
      onToggle={onToggle}
    >
      <Dropdown.Toggle
        as={FilterPillToggle}
        id={id}
        className={filterOptionClass()}
        style={{
          fontSize: "0.875rem",
          width: "100%",
          ...(active ? FILTER_PILL_ACTIVE_STYLE : null),
        }}
        aria-label={ariaLabel ?? label}
      >
        <span className="d-inline-flex align-items-center gap-2 min-w-0">
          <i
            className={`bi ${icon} flex-shrink-0${active ? "" : " text-dark"}`}
            style={{
              color: active ? "#075CCB" : undefined,
              filter: "drop-shadow(0 1px 1px rgba(0,0,0,.35))",
            }}
            aria-hidden
          />
          <span className="text-truncate">{label}</span>
        </span>
        <BsChevronDown
          size={12}
          className="flex-shrink-0"
          style={{
            color: active ? "#075CCB" : undefined,
            filter: "drop-shadow(0 1px 1px rgba(0,0,0,.35))",
          }}
          aria-hidden
        />
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="p-2 shadow-sm"
        popperConfig={FILTER_DROPDOWN_POPPER_CONFIG}
      >
        {children}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export function SearchableFilterDropdown({
  id,
  value,
  options,
  menuSearch,
  onMenuSearch,
  onSelect,
  onClear,
  icon,
  label,
  placeholder,
  clearLabel,
  emptyLabel,
  style,
  autoClose = "outside",
  show,
  onToggle,
}: {
  id: string;
  value: string;
  options: string[];
  menuSearch: string;
  onMenuSearch: (v: string) => void;
  onSelect: (v: string) => void;
  onClear: () => void;
  icon: string;
  label: string;
  placeholder: string;
  clearLabel: string;
  emptyLabel: string;
  style?: CSSProperties;
  autoClose?: boolean | "inside" | "outside";
  show?: boolean;
  onToggle?: (next: boolean) => void;
}) {
  const q = menuSearch.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => o.toLowerCase().includes(q))
    : options;

  return (
    <FilterDropdown
      id={id}
      active={Boolean(value)}
      label={value || label}
      ariaLabel={label}
      icon={icon}
      style={style}
      autoClose={autoClose}
      show={show}
      onToggle={onToggle}
    >
      <Form.Control
        type="search"
        size="sm"
        className="mb-2"
        placeholder={placeholder}
        value={menuSearch}
        onChange={(e) => onMenuSearch(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={placeholder}
      />
      <div className="d-flex flex-column gap-1 overflow-auto max-h-360">
        {value && (
          <Dropdown.Item
            as="button"
            type="button"
            className="rounded-3 text-danger small"
            onClick={onClear}
          >
            {clearLabel}
          </Dropdown.Item>
        )}
        {filtered.length > 0 ? (
          filtered.map((opt) => (
            <FilterMenuItem
              key={opt}
              selected={value === opt}
              className="rounded-3 text-truncate"
              onClick={() => onSelect(opt)}
            >
              {opt}
            </FilterMenuItem>
          ))
        ) : (
          <div className="text-muted small px-2 py-1">{emptyLabel}</div>
        )}
      </div>
    </FilterDropdown>
  );
}
